import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const LETTER_PROMPTS = [
  "What do you want to remember about today?",
  "What would you tell yourself on a hard day?",
  "What are you proud of right now?",
  "What helps you when everything feels heavy?",
  "What are you hoping for?",
];
const WHEN = [["1w", "In 1 week", 7], ["1m", "In 1 month", 30], ["3m", "In 3 months", 91], ["6m", "In 6 months", 182], ["1y", "In 1 year", 365]];
const MAX = 2000;

const isoIn = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const tomorrow = () => isoIn(1);

const WhenPicker = ({ when, setWhen, custom, setCustom }) => (
  <div className="flex flex-wrap gap-2">
    {WHEN.map(([k, l]) => <button type="button" key={k} onClick={() => setWhen(when === k ? null : k)} className={`mm-chip !text-sm ${when === k ? "mm-chip-on" : ""}`} data-testid={`letter-when-${k}`}>{l}</button>)}
    <button type="button" onClick={() => setWhen(when === "custom" ? null : "custom")} className={`mm-chip !text-sm ${when === "custom" ? "mm-chip-on" : ""}`} data-testid="letter-when-custom">Pick a date</button>
    {when === "custom" && <input type="date" className="mm-input !py-1.5 !w-auto text-sm" min={tomorrow()} max={isoIn(365 * 5)} value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="letter-custom-date" />}
  </div>
);

// Write a sealed letter to future me, delivered on a date and/or on the next hard check-in
export const LetterComposer = ({ replyTo, onSaved, onCancel }) => {
  const [body, setBody] = useState("");
  const [prompt, setPrompt] = useState(replyTo ? "A reply to my past self" : null);
  const [when, setWhen] = useState("3m");
  const [custom, setCustom] = useState("");
  const [hardDay, setHardDay] = useState(true);
  const [busy, setBusy] = useState(false);

  const deliverOn = when === "custom" ? custom : when ? isoIn(WHEN.find(([k]) => k === when)[2]) : null;

  const seal = async (e) => {
    e.preventDefault();
    if (!deliverOn && !hardDay) return toast.error("Choose when the letter should arrive");
    setBusy(true);
    try {
      await api.post("/letters", { body, prompt, deliver_on: deliverOn || null, on_hard_day: hardDay, reply_to: replyTo || null });
      toast.success("Letter sealed 💌 It'll find you when the time comes.");
      onSaved();
    } catch (err) { toast.error(errMsg(err, "Couldn't seal your letter")); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={seal} className="mm-card p-5 sm:p-6 mm-fade-up" data-testid="letter-composer">
      <div className="font-semibold">{replyTo ? "Write back to your future self" : "Dear future me…"}</div>
      {!replyTo && (
        <div className="flex flex-wrap gap-2 mt-3">
          {LETTER_PROMPTS.map((p, i) => <button type="button" key={p} onClick={() => setPrompt(prompt === p ? null : p)} className={`mm-chip !text-xs ${prompt === p ? "mm-chip-on" : ""}`} data-testid={`letter-prompt-${i}`}>{p}</button>)}
        </div>
      )}
      {prompt && <p className="text-sm text-mm-primary font-medium mt-3 italic" data-testid="letter-chosen-prompt">{prompt}</p>}
      <textarea className="mm-paper w-full rounded-2xl p-4 mt-3 min-h-[220px] leading-8 text-[15px] outline-none focus:ring-2 focus:ring-mm-primary/40 border border-mm-primary/10"
        maxLength={MAX} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write something kind. Future you will read this when it arrives." data-testid="letter-body" />
      <div className="text-xs text-mm-muted text-right">{body.length}/{MAX}</div>
      <div className="mt-4">
        <div className="text-sm font-semibold mb-2">When should it arrive?</div>
        <WhenPicker when={when} setWhen={setWhen} custom={custom} setCustom={setCustom} />
        <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="letter-hard-day-label">
          <input type="checkbox" checked={hardDay} onChange={(e) => setHardDay(e.target.checked)} className="w-4 h-4 accent-[var(--mm-primary)]" data-testid="letter-hard-day" />
          On my next hard check-in <span className="text-mm-muted">(whichever comes first)</span>
        </label>
      </div>
      <p className="text-xs text-mm-muted mt-3">Once sealed, you can't reread it until it arrives, but you can cancel it any time.</p>
      <div className="flex gap-2 justify-end mt-4">
        {onCancel && <button type="button" className="mm-btn-ghost" onClick={onCancel} data-testid="letter-cancel">Cancel</button>}
        <button className="mm-btn" disabled={busy || !body.trim() || (when === "custom" && !custom && !hardDay)} data-testid="letter-seal-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}Seal letter
        </button>
      </div>
    </form>
  );
};
